import api from "@/api/api";
import showConfirm from "@/components/utils/ui_v4/alerts/confirm/confirm";
import { toast } from "@/components/utils/ui_v4/alerts/toast/toast";
import useCotacaoStore from "@/components/webcotacao/composables/useCotacaoStore";
import {
  API_ENDPOINTS,
  getDecisaoColor,
  getSituacaoColor,
  getStatusColor,
  getStatusIcon,
  TABS
} from "@/components/webcotacao/constants";
import { formatDate, parseDate } from "@/components/webcotacao/utils/dateUtils";
import alturaDaJanela from '@/helpers/ResponsiveHeightReativo';
import { computed, onMounted, reactive, ref } from 'vue';

export default function useCotacoesList() {
  const tab = ref(0);
  const search = ref('');
  const selected = ref([]);
  const listFilter = ref(false);
  const openEditCotacao = ref({ isOpen: false, idCotacao: null });
  const openDetalhesCotacao = ref({ isOpen: false, idCotacao: null });

  // Filtros
  const selectedPeriod = ref({ start: null, end: null });
  const emptyForm = () => ({
    status: null,
    empresa: null,
    fornecedor: null,
  });
  const filters = reactive(emptyForm());

  // Carousel states
  const carouselWrapper = ref(null);
  const carouselScrollLeft = ref(0);
  const carouselMaxScroll = ref(0);

  // Usar store compartilhado
  const store = useCotacaoStore();

  // Dados auxiliares extraídos das cotações
  const filiais = ref([]);
  const fornecedores = ref([]);
  const status = ref([]);

  //Headers da tabela
  const headers = [
    { text: 'ID/Título', value: 'cotacao_transacao_id', sortable: true, width: 250 },
    { text: 'Status da cotação', value: 'nome_status', sortable: true, width: 180 },
    { text: 'Empresa/Filial', value: 'empresa', sortable: true, width: 300 },
    { text: 'Responsável', value: 'nome_completo', sortable: true, width: 200 },
    { text: 'Perido (Inicio/Termino)', value: 'inicio_periodo_recebimento_proposta', sortable: true, width: 200 },
    { text: 'Itens', value: 'qtd_produtos', sortable: true, width: 100 },
    { text: 'Fornecedores', value: 'fornecedores', sortable: true, width: 150 },
    { text: 'Propostas recebidas', value: 'propostas_recebidas', sortable: true, width: 180 },
    { text: 'Decisão da cotação', value: 'nome_decisao', sortable: true, width: 180 },
    { text: 'Situação dos pedidos', value: 'nome_situacao_pedidos', sortable: true, width: 180 },
    { text: 'Última atualização', value: 'updated_at', sortable: true, width: 160 },
    { text: 'Ações', value: 'actions', sortable: false, width: 200, align: 'center' },
  ];

  const extractAuxiliaryData = () => {
    const newFiliais = [];
    const newFornecedores = [];
    const newStatus = [];

    store.cotacoes.value.forEach(item => {
      if (item._original) {
        if (item._original.locais_recebimento && Array.isArray(item._original.locais_recebimento)) {
          newFiliais.push(...item._original.locais_recebimento);
        }
        if (item._original.fornecedores && Array.isArray(item._original.fornecedores)) {
          newFornecedores.push(...item._original.fornecedores);
        }
        if (item._original.cotacao) {
          newStatus.push({
            nome_status: item._original.cotacao.nome_status || 'Rascunho'
          });
        }
      }
    });

    // Remover duplicados
    const mergeUnique = (existing, newItems, key) => {
      const map = new Map(existing.map(i => [i[key], i]));
      newItems.forEach(i => map.set(i[key], i));
      return Array.from(map.values());
    };

    filiais.value = mergeUnique(filiais.value, newFiliais, 'idfilial');
    fornecedores.value = mergeUnique(fornecedores.value, newFornecedores, 'id');
    status.value = mergeUnique(status.value, newStatus, 'nome_status');
  };

  const getCotacoes = async (isLoadMore = false) => {
    await store.fetchCotacoes(isLoadMore);
    extractAuxiliaryData();
  };

  // Aceita "05/02/2026" ou "05/02/2026 14:32:00" e retorna a data (início do dia para comparação)
  const parseDateFilter = (dateString) => {
    const parsed = parseDate(dateString);
    if (!parsed) return null;
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  };

  const filteredCotacoes = computed(() => {
    let items = [...store.cotacoes.value];

    // 1. Filtro por Aba (Status)
    const currentTabKey = TABS[tab.value].key;
    if (currentTabKey !== 'todas') {
      const statusMap = {
        em_cotacao: 'Em Cotação',
        rascunho: 'Rascunho',
        cancelada: 'Cancelada',
      };
      items = items.filter(c => c.nome_status === statusMap[currentTabKey]);
    }

    // 2. Filtros do Menu
    if (filters.status) {
      items = items.filter(item => item.nome_status === filters.status);
    }
    if (filters.empresa) {
      items = items.filter(item => item.idfilial === filters.empresa);
    }
    if (filters.fornecedor) {
      items = items.filter(item =>
        item._original.fornecedores.some(f => f.id === filters.fornecedor)
      );
    }

    // 3. Filtro por Período: considera o "início do período de recebimento de propostas"
    // da cotação (inicio_periodo_recebimento_proposta). Exibe apenas cotações cuja data
    // de início do período está dentro do intervalo [start, end] selecionado.
    // Usa horário local para evitar diferença de 1 dia (UTC vs local) e funcionar com mesmo dia.
    const { start, end } = selectedPeriod.value;
    if (start && end) {
      const [y1, m1, d1] = start.split('-').map(Number);
      const [y2, m2, d2] = end.split('-').map(Number);
      const startDate = new Date(y1, m1 - 1, d1, 0, 0, 0, 0);
      const endDate = new Date(y2, m2 - 1, d2, 23, 59, 59, 999);

      items = items.filter(item => {
        const itemDate = parseDateFilter(item.inicio_periodo_recebimento_proposta);
        return itemDate && itemDate >= startDate && itemDate <= endDate;
      });
    }

    // 4. Filtro por Busca (Texto) — apenas ID e título
    if (search.value) {
      const query = search.value.toLowerCase().trim();
      items = items.filter(item => {
        const idStr = String(item.cotacao_transacao_id != null ? item.cotacao_transacao_id : '').toLowerCase();
        const titulo = String(item.cotacao_titulo != null ? item.cotacao_titulo : '').toLowerCase();
        return idStr.includes(query) || titulo.includes(query);
      });
    }

    return items;
  });

  const activeFilters = computed(() => {
    const active = [];
    if (filters.status) {
      active.push({ key: 'status', label: 'Status', value: filters.status });
    }
    if (filters.empresa) {
      const empresaObj = filiais.value.find(f => f.idfilial === filters.empresa);
      active.push({ key: 'empresa', label: 'Empresa', value: empresaObj ? (empresaObj.razao_social || empresaObj.nome_fantasia) : '' });
    }
    if (filters.fornecedor) {
      const fornecedorObj = fornecedores.value.find(f => f.id === filters.fornecedor);
      active.push({ key: 'fornecedor', label: 'Fornecedor', value: fornecedorObj ? fornecedorObj.razao_social : '' });
    }
    if (selectedPeriod.value.start && selectedPeriod.value.end) {
      const toDDMMYYYY = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : formatDate(s);
      const startBR = toDDMMYYYY(selectedPeriod.value.start);
      const endBR = toDDMMYYYY(selectedPeriod.value.end);
      active.push({ key: 'periodo', label: 'Período', value: `${startBR} - ${endBR}` });
    }
    return active;
  });

  const removeFilter = (key) => {
    if (key === 'periodo') {
      selectedPeriod.value = { start: null, end: null };
    } else if (filters.hasOwnProperty(key)) {
      filters[key] = null;
    }
  };

  const clearFilters = () => {
    Object.assign(filters, emptyForm());
    selectedPeriod.value = { start: null, end: null };
  };

  const carouselCards = computed(() => {
    return store.cotacoes.value.filter(c => c.nome_status === 'Em Cotação').slice(0, 10);
  });

  const tableHeight = computed(() => {
    return carouselCards.value.length > 0 ? alturaDaJanela.value - 580 : alturaDaJanela.value - 325;
  });

  function scrollCarousel(direction) {
    if (!carouselWrapper.value) return;
    const scrollAmount = 320;
    const currentScroll = carouselWrapper.value.scrollLeft;
    if (direction === 'left') {
      carouselWrapper.value.scrollTo({ left: currentScroll - scrollAmount, behavior: 'smooth' });
    } else {
      carouselWrapper.value.scrollTo({ left: currentScroll + scrollAmount, behavior: 'smooth' });
    }
  }

  function updateScrollPosition() {
    if (!carouselWrapper.value) return;
    const wrapper = carouselWrapper.value;
    carouselScrollLeft.value = wrapper.scrollLeft;
    carouselMaxScroll.value = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
  }


  async function deleteItem(item) {
    showConfirm({
      title: 'Excluir cotação',
      message: `Tem certeza que deseja excluir a cotação #${item.cotacao_transacao_id}? Esta ação não pode ser desfeita.`,
      button: {
        yes: 'Confirmar',
        no: 'Cancelar'
      },
      callback: async (confirmed) => {
        if (confirmed) {
          try {
            const { data } = await api.delete(`/cotacao/criar_cotacao_deletar_cotacao/${item.cotacao_transacao_id}`);

            if (data.success) {
              // Recarregar a lista de cotações
              await getCotacoesMarket();
            } else {
              
              toast.error(data.message || 'Erro ao excluir cotação. Tente novamente.');
            }
          } catch (error) {
            
            toast.error('Erro ao excluir cotação. Tente novamente.');
          }
        }
      }
    });
  }

  async function cancelItem(item) {
    if (item.nome_status === 'Cancelada' || item.nome_status === 'Concluída') {
      toast.warning('Esta cotação não pode ser cancelada neste status.');
      return;
    }

    showConfirm({
      title: 'Cancelar cotação',
      message: `Tem certeza que deseja cancelar a cotação #${item.cotacao_transacao_id}?`,
      button: {
        yes: 'Confirmar',
        no: 'Cancelar'
      },
      callback: async (confirmed) => {
        if (confirmed) {
          try {
            const url = `${API_ENDPOINTS.ATUALIZAR_COTACAO}/${item.cotacao_transacao_id}`;
            const { data } = await api.put(url, { cotacao_status: 'Cancelada' });

            if (data.success) {
              toast.success('Cotação cancelada com sucesso.');
              await getCotacoesMarket();
            } else {
              
              toast.error(data.message || 'Erro ao cancelar cotação. Tente novamente.');
            }
          } catch (error) {
            
            toast.error('Erro ao cancelar cotação. Tente novamente.');
          }
        }
      }
    });
  }

  function getEmptyStateMessage() {
    const currentTabKey = TABS[tab.value].key;
    switch (currentTabKey) {
      case 'em_cotacao':
        return 'Não há cotações em andamento.';
      case 'rascunho':
        return 'Não há rascunhos.';
      case 'cancelada':
        return 'Não há cotações canceladas.';
      case 'todas':
      default:
        return 'Não há cotações.';
    }
  }

  const getCotacoesMarket = async () => {
    await store.sincronizarFornecedores();
    extractAuxiliaryData();
    window.location.reload();
  };

  const loadNextPage = async (entries, observer, isIntersecting) => {
    const shouldLoad = isIntersecting === undefined ? true : isIntersecting;
    if (shouldLoad) {
      await store.loadNextPage();
      extractAuxiliaryData();
    }
  };

  // Inicialização
  onMounted(async () => {
    await getCotacoes();

    // Inicializar carrossel após dados carregarem
    await new Promise(resolve => setTimeout(resolve, 100));
    updateScrollPosition();
  });

  return {
    tab,
    search,
    selected,
    isLoading: store.isLoading,
    listFilter,
    tableHeight,
    selectedPeriod,
    filters,
    status,
    filiais,
    fornecedores,
    tabs: TABS,
    headers,
    filteredCotacoes,
    carouselCards,
    carouselWrapper,
    carouselScrollLeft,
    carouselMaxScroll,
    getStatusColor,
    getStatusIcon,
    getDecisaoColor,
    getSituacaoColor,
    deleteItem,
    cancelItem,
    scrollInfo: store.scrollInfo,
    loadNextPage,
    openEditCotacao,
    openDetalhesCotacao,
    getCotacoes,
    activeFilters,
    removeFilter,
    clearFilters,
    scrollCarousel,
    updateScrollPosition,
    getEmptyStateMessage,
    getCotacoesMarket
  };
}
